// Verwaltung der Selbstauskunft-Links — läuft als Sheet über der Selbstauskunft-Seite
// (Handoff Selbstauskunft §3; Muster: BuchungslinksVerwaltung — Liste ↔ Editor).
// Öffentliche Gegenseite: pages/public/SelbstauskunftSeite.tsx
// (/selbstauskunft/:slug), gespeist von der Edge Function `selbstauskunft`.
// Anders als Buchungslinks: keine Slot/Regeln/Dauer-Felder — nur Titel,
// Beschreibung, optionales Objekt/Einheit und Aktiv-Schalter.
import { useEffect, useState, type CSSProperties } from 'react';
import { supabase } from '../../../lib/supabase/client';
import {
  Buildings, CaretUpDown, Check, CircleNotch, Copy, GlobeSimple, LinkSimple, MagnifyingGlass, PencilSimple, Plus, StackPlus, Trash, X,
} from '@phosphor-icons/react';
import { Sheet } from '../../../components/ui/Sheet';
import { IosSchalter } from '../../../components/ui/IosSchalter';
import { BestaetigenDialog } from '../../../components/immobilien/ui';
import { useMobilModus } from '../../../lib/immobilien/mobil';

const LABEL: CSSProperties = { fontSize: 11.5, fontWeight: 700, color: 'var(--ink3)' };
const GRUPPE: CSSProperties = { borderRadius: 20, background: 'var(--glass)', boxShadow: 'var(--glass-sh)' };
const ICON_KNOPF: CSSProperties = { width: 34, height: 34, borderRadius: 11, border: 'none', cursor: 'pointer', flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' };
const PRIMAER: CSSProperties = { borderRadius: 15, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 14, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
  background: 'var(--acc-fill)', boxShadow: 'var(--acc-sh)', color: 'var(--acc-ink)' };

interface SelbstauskunftLink {
  id: string;
  slug: string;
  titel: string;
  beschreibung: string | null;
  objekt_id: string | null;
  einheit_id: string | null;
  aktiv: boolean;
  created_at: string;
  objekt?: { id: string; bezeichnung: string } | null;
  einheit?: { id: string; bezeichnung: string } | null;
}

interface Entwurf {
  id: string | null;
  slug: string | null;
  titel: string;
  beschreibung: string;
  objektId: string;
  einheitId: string;
  aktiv: boolean;
}

const leererEntwurf = (): Entwurf => ({
  id: null,
  slug: null,
  titel: '',
  beschreibung: '',
  objektId: '',
  einheitId: '',
  aktiv: true,
});

// Slug clientseitig erzeugen — 8 Zeichen a-z0-9; unique-Constraint in der DB fängt
// die (extrem seltene) Kollision beim Insert ab.
const generiereSlug = (): string =>
  Array.from(crypto.getRandomValues(new Uint8Array(8)))
    .map((b) => 'abcdefghijklmnopqrstuvwxyz0123456789'[b % 36])
    .join('');

export interface SelbstauskunftLinksVerwaltungProps {
  onSchliessen: () => void;
  /** Rückmeldung als Toast der Seite (Erfolg/Fehler) */
  onToast: (text: string, fehler?: boolean) => void;
}

export default function SelbstauskunftLinksVerwaltung({ onSchliessen, onToast }: SelbstauskunftLinksVerwaltungProps) {
  const [links, setLinks] = useState<SelbstauskunftLink[]>([]);
  const [laedt, setLaedt] = useState(true);
  const [entwurf, setEntwurf] = useState<Entwurf | null>(null);
  const [speichert, setSpeichert] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [kopiertId, setKopiertId] = useState<string | null>(null);
  const [loeschFrage, setLoeschFrage] = useState(false);
  // Handy: Knöpfe 44 px und in eigener Zeile, damit der Titel lesbar bleibt
  const { mobil } = useMobilModus();
  const KNOPF: CSSProperties = mobil ? { ...ICON_KNOPF, width: 44, height: 44, borderRadius: 14 } : ICON_KNOPF;

  // Objekt-Typeahead (Muster BuchungslinksVerwaltung/TerminFormular).
  const [objektSearch, setObjektSearch] = useState('');
  const [objektResults, setObjektResults] = useState<{ id: string; bezeichnung: string; strasse: string; hausnummer: string }[]>([]);
  const [selectedObjekt, setSelectedObjekt] = useState<{ id: string; bezeichnung: string } | null>(null);
  const [einheiten, setEinheiten] = useState<{ id: string; bezeichnung: string }[]>([]);

  const lade = async () => {
    const { data, error } = await supabase
      .from('selbstauskunft_links')
      .select('id, slug, titel, beschreibung, objekt_id, einheit_id, aktiv, created_at, objekt:objekte(id, bezeichnung), einheit:objekt_einheiten(id, bezeichnung)')
      .order('created_at', { ascending: false });
    if (!error) setLinks((data as unknown as SelbstauskunftLink[]) || []);
    setLaedt(false);
  };
  useEffect(() => { void lade(); }, []);

  // Objekt-Suche mit Debounce (Muster BuchungslinksVerwaltung).
  useEffect(() => {
    if (objektSearch.length < 2) { setObjektResults([]); return; }
    const t = setTimeout(async () => {
      const { data } = await supabase.from('objekte').select('id, bezeichnung, strasse, hausnummer').ilike('bezeichnung', `%${objektSearch}%`).limit(5);
      setObjektResults(data || []);
    }, 300);
    return () => clearTimeout(t);
  }, [objektSearch]);

  // Einheiten des gewählten Objekts nachladen.
  useEffect(() => {
    if (!entwurf || !entwurf.objektId) { setEinheiten([]); return; }
    (async () => {
      const { data } = await supabase.from('objekt_einheiten').select('id, bezeichnung').eq('objekt_id', entwurf.objektId).order('bezeichnung', { ascending: true });
      setEinheiten(data || []);
    })();
  }, [entwurf?.objektId]);

  const oeffneEditor = (l?: SelbstauskunftLink) => {
    if (l) {
      setEntwurf({
        id: l.id,
        slug: l.slug,
        titel: l.titel,
        beschreibung: l.beschreibung || '',
        objektId: l.objekt_id || '',
        einheitId: l.einheit_id || '',
        aktiv: l.aktiv,
      });
      setSelectedObjekt(l.objekt_id ? { id: l.objekt_id, bezeichnung: l.objekt?.bezeichnung || '' } : null);
    } else {
      setEntwurf(leererEntwurf());
      setSelectedObjekt(null);
    }
    setObjektSearch('');
    setObjektResults([]);
    setFehler(null);
  };

  // Duplizieren: Editor als Neu-Fall mit den Feldern der Vorlage — Speichern erzeugt
  // einen frischen Slug, der Ursprungslink bleibt unangetastet.
  const dupliziere = (l: SelbstauskunftLink) => {
    setEntwurf({
      id: null,
      slug: null,
      titel: l.titel,
      beschreibung: l.beschreibung || '',
      objektId: l.objekt_id || '',
      einheitId: l.einheit_id || '',
      aktiv: true,
    });
    setSelectedObjekt(l.objekt_id ? { id: l.objekt_id, bezeichnung: l.objekt?.bezeichnung || '' } : null);
    setObjektSearch('');
    setObjektResults([]);
    setFehler(null);
  };

  const handleSpeichern = async () => {
    if (!entwurf || speichert) return;
    const titel = entwurf.titel.trim();
    if (!titel) { setFehler('Bitte einen Titel angeben.'); return; }

    setSpeichert(true);
    setFehler(null);
    const zeile = {
      titel,
      beschreibung: entwurf.beschreibung.trim() || null,
      objekt_id: entwurf.objektId || null,
      einheit_id: entwurf.einheitId || null,
      aktiv: entwurf.aktiv,
    };
    const res = entwurf.id
      ? await supabase.from('selbstauskunft_links').update({ ...zeile, updated_at: new Date().toISOString() }).eq('id', entwurf.id)
      : await supabase.from('selbstauskunft_links').insert({ ...zeile, slug: generiereSlug() });
    setSpeichert(false);
    if (res.error) { setFehler(res.error.message); return; }
    onToast(entwurf.id ? 'Link gespeichert' : 'Link angelegt');
    setEntwurf(null);
    await lade();
  };

  const handleLoeschen = async () => {
    // Rückfrage über den App-Dialog (loeschFrage) statt window.confirm.
    if (!entwurf?.id || speichert) return;
    setSpeichert(true);
    const { error } = await supabase.from('selbstauskunft_links').delete().eq('id', entwurf.id);
    setSpeichert(false);
    setLoeschFrage(false);
    if (error) { setFehler(error.message); return; }
    onToast('Link gelöscht');
    setEntwurf(null);
    await lade();
  };

  const kopiereLink = async (l: SelbstauskunftLink) => {
    const url = `${window.location.origin}/selbstauskunft/${l.slug}`;
    try {
      await navigator.clipboard.writeText(url);
      setKopiertId(l.id);
      setTimeout(() => setKopiertId((id) => (id === l.id ? null : id)), 2000);
    } catch {
      onToast(`Link konnte nicht kopiert werden: ${url}`, true);
    }
  };

  const adresse = (slug: string) => `${window.location.origin}/selbstauskunft/${slug}`;
  const kopiereEditor = async (slug: string) => {
    try {
      await navigator.clipboard.writeText(adresse(slug));
      setKopiertId('editor');
      setTimeout(() => setKopiertId((id) => (id === 'editor' ? null : id)), 2000);
    } catch { /* Clipboard nicht verfügbar — URL steht sichtbar daneben */ }
  };

  const titel = entwurf ? (entwurf.id ? 'Link bearbeiten' : 'Neuer Selbstauskunft-Link') : 'Selbstauskunft-Links';

  return (
    <Sheet titel={titel} icon={LinkSimple} iconFarbe="#0a84ff" onSchliessen={onSchliessen}
      onZurueck={entwurf ? () => { setEntwurf(null); setFehler(null); } : undefined}>
      {laedt ? (
        <div className="flex justify-center" style={{ padding: '40px 0', color: 'var(--ink3)' }}><CircleNotch size={20} weight="bold" className="animate-spin" /></div>
      ) : entwurf ? (
        <>
          {entwurf.id && entwurf.slug && (
            <div className="flex flex-col" style={{ gap: 8, padding: '12px 14px', borderRadius: 18, background: 'var(--acc-soft)' }}>
              <div className="flex items-center" style={{ gap: 8 }}>
                <LinkSimple size={15} weight="bold" color="var(--acc)" className="shrink-0" />
                <span className="min-w-0 flex-1 truncate" style={{ fontSize: 12.5, fontWeight: 700 }}>{adresse(entwurf.slug)}</span>
                <button type="button" onClick={() => { void kopiereEditor(entwurf.slug!); }} aria-label="Link kopieren" data-tip="Link kopieren"
                  style={{ ...ICON_KNOPF, width: mobil ? 44 : 32, height: mobil ? 44 : 32, borderRadius: 10, background: 'var(--sheet)', color: kopiertId === 'editor' ? 'var(--ok)' : 'var(--ink2)' }}>
                  {kopiertId === 'editor' ? <Check size={13} weight="bold" /> : <Copy size={13} weight="bold" />}
                </button>
              </div>
              <span style={{ fontSize: 11.5, lineHeight: 1.45, color: 'var(--ink2)' }}>
                Änderungen wirken auf diesen bereits verteilten Link. Für eine neue Vermietung: Link duplizieren oder neu anlegen.
              </span>
            </div>
          )}

          <div className="overflow-hidden" style={GRUPPE}>
            <label className="flex flex-col" style={{ gap: 4, padding: '12px 14px', borderBottom: '1px solid var(--line)' }}>
              <span style={LABEL}>Titel *</span>
              <input type="text" value={entwurf.titel} onChange={(e) => setEntwurf({ ...entwurf, titel: e.target.value })}
                placeholder="z. B. Selbstauskunft — Musterstr. 5, Whg. 3"
                style={{ border: 'none', outline: 'none', background: 'transparent', fontFamily: 'inherit', fontSize: 15, fontWeight: 700, color: 'var(--ink)', minWidth: 0 }} />
            </label>
            <label className="flex flex-col" style={{ gap: 4, padding: '12px 14px' }}>
              <span style={LABEL}>Beschreibung</span>
              <textarea value={entwurf.beschreibung} onChange={(e) => setEntwurf({ ...entwurf, beschreibung: e.target.value })} rows={3}
                placeholder="Öffentlicher Intro-Text auf der Formularseite..."
                style={{ border: 'none', outline: 'none', resize: 'none', background: 'transparent', fontFamily: 'inherit', fontSize: 14, lineHeight: 1.45, color: 'var(--ink)' }} />
            </label>
          </div>

          <div className="flex flex-col" style={{ ...GRUPPE, padding: '12px 14px', gap: 8 }}>
            <span style={LABEL}>Objekt</span>
            {selectedObjekt ? (
              <div className="flex items-center" style={{ gap: 10 }}>
                <span aria-hidden className="inline-flex shrink-0 items-center justify-center" style={{ width: 32, height: 32, borderRadius: 10, background: '#0a84ff', color: '#fff' }}>
                  <Buildings size={16} weight="fill" />
                </span>
                <span className="min-w-0 flex-1 truncate" style={{ fontSize: 14, fontWeight: 800 }}>{selectedObjekt.bezeichnung}</span>
                <button type="button" aria-label="Objekt entfernen" data-tip="Objekt entfernen"
                  onClick={() => { setSelectedObjekt(null); setEntwurf({ ...entwurf, objektId: '', einheitId: '' }); }}
                  style={{ ...ICON_KNOPF, width: mobil ? 44 : 30, height: mobil ? 44 : 30, borderRadius: '50%', background: 'var(--fill2)', color: 'var(--ink2)' }}>
                  <X size={12} weight="bold" />
                </button>
              </div>
            ) : (
              <div className="relative">
                <div className="flex items-center" style={{ height: 44, borderRadius: 13, gap: 8, padding: '0 12px', background: 'var(--fill2)' }}>
                  <MagnifyingGlass size={15} color="var(--ink3)" className="shrink-0" />
                  <input type="text" value={objektSearch} onChange={(e) => setObjektSearch(e.target.value)} placeholder="Objekt suchen..." aria-label="Objekt suchen"
                    style={{ flex: 1, minWidth: 0, border: 'none', outline: 'none', background: 'transparent', fontFamily: 'inherit', fontSize: 14, fontWeight: 600, color: 'var(--ink)' }} />
                </div>
                {objektResults.length > 0 && (
                  <div className="overflow-hidden" style={{ marginTop: 6, borderRadius: 14, background: 'var(--sheet)', boxShadow: '0 14px 30px -12px var(--shade), inset 0 0 0 1px var(--line)' }}>
                    {objektResults.map((o) => (
                      <button key={o.id} type="button"
                        onClick={() => {
                          setSelectedObjekt({ id: o.id, bezeichnung: o.bezeichnung });
                          setEntwurf({ ...entwurf, objektId: o.id, einheitId: '' });
                          setObjektSearch('');
                          setObjektResults([]);
                        }}
                        className="immo-menue-zeile flex w-full items-baseline text-left"
                        style={{ gap: 6, padding: '10px 12px', border: 'none', borderBottom: '1px solid var(--line)', background: 'transparent', cursor: 'pointer', fontFamily: 'inherit', color: 'var(--ink)' }}>
                        <span style={{ fontSize: 13.5, fontWeight: 700 }}>{o.bezeichnung}</span>
                        <span style={{ fontSize: 12, color: 'var(--ink3)' }}>— {o.strasse} {o.hausnummer}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            {entwurf.objektId && (
              <>
                <span style={{ ...LABEL, marginTop: 4 }}>Einheit</span>
                <div className="relative flex items-center" style={{ height: 44, borderRadius: 13, padding: '0 12px', background: 'var(--fill2)' }}>
                  <span className="min-w-0 flex-1 truncate" style={{ fontSize: 14, fontWeight: 700 }}>
                    {einheiten.find((x) => x.id === entwurf.einheitId)?.bezeichnung || '— Keine Einheit —'}
                  </span>
                  <CaretUpDown size={13} weight="bold" color="var(--ink3)" className="pointer-events-none shrink-0" />
                  <select aria-label="Einheit" value={entwurf.einheitId} onChange={(e) => setEntwurf({ ...entwurf, einheitId: e.target.value })}
                    className="absolute inset-0 h-full w-full cursor-pointer opacity-0" style={{ fontSize: 14 }}>
                    <option value="">— Keine Einheit —</option>
                    {einheiten.map((x) => <option key={x.id} value={x.id}>{x.bezeichnung}</option>)}
                  </select>
                </div>
              </>
            )}
          </div>

          <div className="flex items-center" style={{ ...GRUPPE, padding: '12px 14px', gap: 12 }}>
            <span aria-hidden className="inline-flex shrink-0 items-center justify-center" style={{ width: 32, height: 32, borderRadius: 10, background: '#34c759', color: '#fff' }}>
              <GlobeSimple size={16} weight="fill" />
            </span>
            <span className="flex flex-1 flex-col">
              <span style={{ fontSize: 14, fontWeight: 800 }}>Aktiv</span>
              <span style={{ fontSize: 12, color: 'var(--ink2)' }}>Formularseite ist erreichbar</span>
            </span>
            <IosSchalter an={entwurf.aktiv} onChange={(an) => setEntwurf({ ...entwurf, aktiv: an })} label="Aktiv (Formularseite ist erreichbar)" />
          </div>

          {fehler && <span role="alert" style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--bad)' }}>{fehler}</span>}

          <div className="flex" style={{ gap: 8 }}>
            <button type="button" onClick={() => { void handleSpeichern(); }} disabled={speichert} style={{ ...PRIMAER, flex: 1, height: 48, opacity: speichert ? 0.6 : 1 }}>
              {speichert ? <CircleNotch size={15} weight="bold" className="animate-spin" /> : <Check size={15} weight="bold" />} Speichern
            </button>
            {entwurf.id && (
              <button type="button" onClick={() => setLoeschFrage(true)} disabled={speichert} aria-label="Selbstauskunft-Link löschen" data-tip="Selbstauskunft-Link löschen"
                style={{ ...ICON_KNOPF, width: 48, height: 48, borderRadius: 15, background: 'var(--bad-soft)', color: 'var(--bad)' }}>
                <Trash size={17} weight="bold" />
              </button>
            )}
          </div>
        </>
      ) : (
        <>
          <span style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--ink2)' }}>
            Öffentliche Formularseiten — Interessenten füllen ihre Selbstauskunft online aus,
            der Eingang landet strukturiert in der Liste.
          </span>
          <button type="button" onClick={() => oeffneEditor()} style={{ ...PRIMAER, height: 46 }}>
            <Plus size={15} weight="bold" /> Neuer Selbstauskunft-Link
          </button>
          {links.length === 0 ? (
            <span style={{ fontSize: 13, color: 'var(--ink3)', fontStyle: 'italic', padding: '6px 2px' }}>Noch keine Selbstauskunft-Links.</span>
          ) : (
            <div className="flex flex-col overflow-hidden" style={GRUPPE}>
              {links.map((l, i) => {
                const knoepfe = (
                  <span className="flex shrink-0" style={{ gap: mobil ? 8 : 6 }}>
                    <button type="button" onClick={() => { void kopiereLink(l); }} aria-label="Link kopieren" data-tip="Link kopieren – Öffentliche Adresse in die Zwischenablage"
                      style={{ ...KNOPF, background: kopiertId === l.id ? 'var(--ok-soft)' : 'var(--fill2)', color: kopiertId === l.id ? 'var(--ok)' : 'var(--ink2)' }}>
                      {kopiertId === l.id ? <Check size={14} weight="bold" /> : <Copy size={14} weight="bold" />}
                    </button>
                    <button type="button" onClick={() => dupliziere(l)} aria-label="Als neuen Link duplizieren" data-tip="Duplizieren – Als neuen Link mit frischer Adresse anlegen"
                      style={{ ...KNOPF, background: 'var(--fill2)', color: 'var(--ink2)' }}>
                      <StackPlus size={14} weight="bold" />
                    </button>
                    <button type="button" onClick={() => oeffneEditor(l)} aria-label="Bearbeiten" data-tip="Bearbeiten"
                      style={{ ...KNOPF, background: 'var(--fill2)', color: 'var(--ink2)' }}>
                      <PencilSimple size={14} weight="bold" />
                    </button>
                  </span>
                );
                return (
                  <div key={l.id} className={mobil ? 'flex flex-col' : 'flex items-center'}
                    style={{ gap: mobil ? 10 : 12, padding: '12px 12px 12px 14px', borderBottom: i < links.length - 1 ? '1px solid var(--line)' : 'none', opacity: l.aktiv ? 1 : 0.5 }}>
                    <span className="flex min-w-0 flex-1 items-center" style={{ gap: 12 }}>
                      <span aria-hidden className="inline-flex shrink-0 items-center justify-center" style={{ width: 36, height: 36, borderRadius: 11, background: l.aktiv ? '#0a84ff' : '#8e8e93', color: '#fff' }}>
                        <LinkSimple size={16} weight="bold" />
                      </span>
                      <span className="flex min-w-0 flex-1 flex-col" style={{ gap: 1 }}>
                        <span className={mobil ? 'break-words' : 'truncate'} style={{ fontSize: 14, fontWeight: 800 }}>{l.titel}</span>
                        <span className="truncate" style={{ fontSize: 12, color: 'var(--ink2)' }}>
                          {l.objekt?.bezeichnung || 'Kein Objekt zugeordnet'}{!l.aktiv ? ' · inaktiv' : ''}
                        </span>
                      </span>
                    </span>
                    {mobil ? <span className="flex justify-end">{knoepfe}</span> : knoepfe}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {loeschFrage && entwurf && (
        <BestaetigenDialog titel="Link löschen?" icon={Trash} bestaetigenLabel="Löschen" laeuft={speichert}
          text={`Selbstauskunft-Link „${entwurf.titel}“ wirklich löschen?`}
          onAbbrechen={() => setLoeschFrage(false)} onBestaetigen={() => { void handleLoeschen(); }} />
      )}
    </Sheet>
  );
}
